import produce from 'immer';
import * as ACTIONS from '../../actionTypes/priceOfferListActionTypes';
import filterTypes from '../../../constants/filterTypes';

const initialState = {
  filterOptions: {
    departureMonthOptions: [],
    departureAirportOptions: [],
    minPrice: 0,
    maxPrice: 0,
    destinationTree: [],
    idToDestinationMap: {},
  },
  selectedFilters: {
    selectedDepartureMonths: [],
    selectedDepartureAirports: [],
    selectedMaxPrice: null,
    selectedDestinations: [],
  },
  selectedFiltersInOrderOfSelection: [], // For displaying filter tags
};

const buildDestinationTreeAndMap = (destinationList) => {
  const idToIndexMap = {};
  destinationList.forEach((destination, index) => {
    idToIndexMap[destination.id] = index;
  });

  const tree = [];
  const idToDestinationMap = {};
  destinationList.forEach((destination) => {
    idToDestinationMap[destination.id] = destination;
    if (destination.parentId === null) {
      tree.push(destination);
      return;
    }
    const parentDestination = destinationList[idToIndexMap[destination.parentId]];
    parentDestination.children = [...(parentDestination.children || []), destination];
  });
  return [tree, idToDestinationMap];
};

const removeAllSelectedParents = (destination, draft) => {
  const { parentId } = destination;
  if (parentId === null) {
    return;
  }
  // Remove parent destination from selected filters if selected
  const parentIsSelectedIndex = draft.selectedFilters.selectedDestinations.findIndex(currentDestination => currentDestination.id === parentId);
  if (parentIsSelectedIndex !== -1) {
    draft.selectedFilters.selectedDestinations.splice(parentIsSelectedIndex, 1);
  }
  // Remove parent destination from filtertags
  const selectedTagIndex = draft.selectedFiltersInOrderOfSelection.findIndex(filter => filter.filterType === filterTypes.destination && filter.value === parentId);
  if (selectedTagIndex !== -1) {
    draft.selectedFiltersInOrderOfSelection.splice(selectedTagIndex, 1);
  }

  const parentDestination = draft.filterOptions.idToDestinationMap[parentId];
  removeAllSelectedParents(parentDestination, draft);
};

const removeAllSelectedChildren = (destination, draft) => {
  const { children } = destination;
  if (!children) {
    return;
  }
  children.forEach((currentChild) => {
    // Remove child destination from selected filters if selected
    const childIsSelectedIndex = draft.selectedFilters.selectedDestinations.findIndex(currentDestination => currentDestination.id === currentChild.id);
    if (childIsSelectedIndex !== -1) {
      draft.selectedFilters.selectedDestinations.splice(childIsSelectedIndex, 1);
    }
    // Remove child destination from filtertags
    const selectedTagIndex = draft.selectedFiltersInOrderOfSelection.findIndex(filter => filter.filterType === filterTypes.destination && filter.value === currentChild.id);
    if (selectedTagIndex !== -1) {
      draft.selectedFiltersInOrderOfSelection.splice(selectedTagIndex, 1);
    }
    removeAllSelectedChildren(currentChild, draft);
  });
};

export default produce((draft, action) => {
  switch (action.type) {
    case ACTIONS.INIT_SUCCESS:
      draft.filterOptions.departureMonthOptions = action.filterOptions.departureMonthList;
      draft.filterOptions.departureAirportOptions = action.filterOptions.departureAirportList;
      draft.filterOptions.minPrice = action.filterOptions.minPrice;
      draft.filterOptions.maxPrice = action.filterOptions.maxPrice;
      [draft.filterOptions.destinationTree, draft.filterOptions.idToDestinationMap] = buildDestinationTreeAndMap(action.filterOptions.destinationList);
      break;
    case ACTIONS.ADD_DEPARTURE_MONTH_FILTER: {
      const { selectedDepartureMonths } = draft.selectedFilters;
      const { monthToAdd } = action;
      if (!selectedDepartureMonths.includes(monthToAdd)) {
        draft.selectedFilters.selectedDepartureMonths.push(monthToAdd);
        draft.selectedFiltersInOrderOfSelection.push({ value: monthToAdd, filterType: filterTypes.departureMonth });
      }
      break;
    }
    case ACTIONS.REMOVE_DEPARTURE_MONTH_FILTER: {
      const { selectedDepartureMonths } = draft.selectedFilters;
      const { monthToRemove } = action;
      if (selectedDepartureMonths.includes(monthToRemove)) {
        draft.selectedFilters.selectedDepartureMonths.splice(selectedDepartureMonths.indexOf(monthToRemove), 1);
        const index = draft.selectedFiltersInOrderOfSelection.findIndex(filter => filter.filterType === filterTypes.departureMonth && filter.value === monthToRemove);
        draft.selectedFiltersInOrderOfSelection.splice(index, 1);
      }
      break;
    }
    case ACTIONS.ADD_DEPARTURE_AIRPORT_FILTER: {
      const { selectedDepartureAirports } = draft.selectedFilters;
      const { airportToAdd } = action;
      if (!selectedDepartureAirports.includes(airportToAdd)) {
        draft.selectedFilters.selectedDepartureAirports.push(airportToAdd);
        draft.selectedFiltersInOrderOfSelection.push({ value: airportToAdd, filterType: filterTypes.departureAirport });
      }
      break;
    }
    case ACTIONS.REMOVE_DEPARTURE_AIRPORT_FILTER: {
      const { selectedDepartureAirports } = draft.selectedFilters;
      const { airportToRemove } = action;
      if (selectedDepartureAirports.includes(airportToRemove)) {
        draft.selectedFilters.selectedDepartureAirports.splice(draft.selectedFilters.selectedDepartureAirports.indexOf(airportToRemove), 1);
        const index = draft.selectedFiltersInOrderOfSelection.findIndex(filter => filter.filterType === filterTypes.departureAirport && filter.value === airportToRemove);
        draft.selectedFiltersInOrderOfSelection.splice(index, 1);
      }
      break;
    }
    case ACTIONS.SET_MAX_PRICE_FILTER: {
      const { maxPrice } = action;
      draft.selectedFilters.selectedMaxPrice = maxPrice;

      const index = draft.selectedFiltersInOrderOfSelection.findIndex(filter => filter.filterType === filterTypes.price);
      if (index < 0) {
        draft.selectedFiltersInOrderOfSelection.push({ value: maxPrice, filterType: filterTypes.price });
      } else {
        draft.selectedFiltersInOrderOfSelection[index] = { value: maxPrice, filterType: filterTypes.price };
      }
      break;
    }
    case ACTIONS.REMOVE_MAX_PRICE_FILTER: {
      draft.selectedFilters.selectedMaxPrice = null;
      const index = draft.selectedFiltersInOrderOfSelection.findIndex(filter => filter.filterType === filterTypes.price);
      if (index >= 0) {
        draft.selectedFiltersInOrderOfSelection.splice(index, 1);
      }
      break;
    }
    case ACTIONS.ADD_DESTINATION_FILTER: {
      const { destinationToAdd } = action;
      if (!draft.selectedFilters.selectedDestinations.some(destination => destination.id === destinationToAdd.id)) {
        // Add destination
        draft.selectedFilters.selectedDestinations.push(destinationToAdd);
        draft.selectedFiltersInOrderOfSelection.push({ value: destinationToAdd.id, filterType: filterTypes.destination });
        removeAllSelectedParents(destinationToAdd, draft);
        removeAllSelectedChildren(destinationToAdd, draft);
      }
      break;
    }
    case ACTIONS.REMOVE_DESTINATION_FILTER: {
      const { destinationToRemove } = action;
      const index = draft.selectedFilters.selectedDestinations.findIndex(
        destination => destination.id === destinationToRemove.id,
      );
      if (index >= 0) {
        draft.selectedFilters.selectedDestinations.splice(index, 1);
      }

      const tagIndex = draft.selectedFiltersInOrderOfSelection.findIndex(
        tag => tag.filterType === filterTypes.destination && tag.value === destinationToRemove.id,
      );
      if (tagIndex >= 0) {
        draft.selectedFiltersInOrderOfSelection.splice(tagIndex, 1);
      }
      break;
    }
    case ACTIONS.CLEAR_ALL_FILTERS:
      draft.selectedFilters = initialState.selectedFilters;
      draft.selectedFiltersInOrderOfSelection = initialState.selectedFiltersInOrderOfSelection;
      break;
    default:
  }
}, initialState);
